import React from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";

import { usePolicyCommands } from "@dashboard/devices/components/Commands/hooks";
import { MdmPolicyCommand } from "@devices/types";
import { Box, Collapse, IconButton, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { SelectFileField } from "@shared/form/SelectFileField";
import { showErrorToast } from "@shared/toasts/Toasts";

import { useStyles } from "./ImportGroupPolicyDialog.style";
import { getPolicyCommandLabels, isPolicyCommandRecognized, PolicyCommandLabels } from "./policyCommandDisplay";

interface Props {
    open: boolean;
    groupId: number;
    policyCommands: MdmPolicyCommand[];
    onClose: () => void;
}

type FormValues = {
    files: FileList;
};

type PreviewEntryData = {
    raw: unknown;
    payload: MdmPolicyCommand | null;
    caption: string | null;
    commandName: string | null;
    recognized: boolean;
};

type PreviewEntry = PreviewEntryData & {
    labels: PolicyCommandLabels;
};

type ParseErrorKey = "invalidJson" | "notAnArray" | "emptyFile";

type ParseState =
    | { status: "idle" }
    | { status: "error"; errorKey: ParseErrorKey }
    | { status: "ready"; entries: PreviewEntryData[] };

const readFileContents = async (file: File): Promise<string | null> => {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
        reader.onload = (event) => {
            resolve(event.target?.result?.toString() ?? null);
        };
        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsText(file);
    });
};

const toPreviewEntry = (item: unknown): PreviewEntryData => {
    const invalidEntry: PreviewEntryData = { raw: item, payload: null, caption: null, commandName: null, recognized: false };

    if (typeof item !== "object" || item === null || Array.isArray(item)) {
        return invalidEntry;
    }

    const record = item as Record<string, unknown>;
    const caption = typeof record.caption === "string" && record.caption ? record.caption : null;

    if (typeof record.command !== "string" || !record.command.trim()) {
        return { ...invalidEntry, caption };
    }

    const payload = item as MdmPolicyCommand;

    return {
        raw: item,
        payload,
        caption,
        commandName: record.command,
        recognized: isPolicyCommandRecognized(payload),
    };
};

const toPreviewEntryLabels = (entry: PreviewEntryData): PolicyCommandLabels =>
    entry.payload
        ? getPolicyCommandLabels(entry.payload)
        : getPolicyCommandLabels({ caption: entry.caption ?? undefined } as MdmPolicyCommand);

const parseFileContents = (contents: string): ParseState => {
    let parsed: unknown;

    try {
        parsed = JSON.parse(contents);
    } catch (_error) {
        return { status: "error", errorKey: "invalidJson" };
    }

    if (!Array.isArray(parsed)) {
        return { status: "error", errorKey: "notAnArray" };
    }
    if (!parsed.length) {
        return { status: "error", errorKey: "emptyFile" };
    }

    return { status: "ready", entries: parsed.map(toPreviewEntry) };
};

const EntryStatusIcon = ({ entry }: { entry: PreviewEntryData }) => {
    const { t } = useTranslation(["modals"]);
    const { classes } = useStyles();

    if (!entry.commandName) {
        const label = t("modals:importGroupPolicy.preview.invalidEntry");
        return (
            <Tooltip title={label}>
                <i className={`fa-solid fa-circle-exclamation ${classes.statusIconError}`} role="img" aria-label={label} />
            </Tooltip>
        );
    }
    if (!entry.recognized) {
        const label = t("modals:importGroupPolicy.preview.unrecognizedCommand");
        return (
            <Tooltip title={label}>
                <i className={`fa-solid fa-triangle-exclamation ${classes.statusIconWarning}`} role="img" aria-label={label} />
            </Tooltip>
        );
    }
    const label = t("modals:importGroupPolicy.preview.validEntry");
    return <i className={`fa-solid fa-check ${classes.statusIconSuccess}`} role="img" aria-label={label} />;
};

const ParseErrorAlert = ({ errorKey }: { errorKey: ParseErrorKey }) => {
    const { t } = useTranslation(["modals"]);
    const { classes } = useStyles();

    // Static literal keys so the i18next extractor keeps them
    const messages: Record<ParseErrorKey, string> = {
        invalidJson: t("modals:importGroupPolicy.error.invalidJson"),
        notAnArray: t("modals:importGroupPolicy.error.notAnArray"),
        emptyFile: t("modals:importGroupPolicy.error.emptyFile"),
    };

    return (
        <CustomAlert severity="error" className={classes.parseErrorAlert}>
            {messages[errorKey]}
        </CustomAlert>
    );
};

const PolicyImportPreviewItem = ({ entry, index, divider }: { entry: PreviewEntry; index: number; divider: boolean }) => {
    const { t } = useTranslation(["modals"]);
    const { classes } = useStyles();

    const [open, setOpen] = React.useState(false);

    return (
        <>
            <ListItem divider={divider && !open}>
                <ListItemIcon className={classes.leadingIcons}>
                    <IconButton
                        size="small"
                        onClick={() => setOpen((prev) => !prev)}
                        aria-label={t("modals:importGroupPolicy.preview.toggleDetails")}
                    >
                        <i className={`fa-solid ${open ? "fa-chevron-down" : "fa-chevron-right"} ${classes.chevron}`} />
                    </IconButton>
                    <Typography variant="body2" color="textSecondary" className={classes.indexNumber}>
                        {index + 1}
                    </Typography>
                </ListItemIcon>
                <ListItemText primary={entry.labels.primary} secondary={entry.labels.secondary} className={classes.entryText} />
                <ListItemIcon className={classes.statusIconWrapper}>
                    <EntryStatusIcon entry={entry} />
                </ListItemIcon>
            </ListItem>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box className={classes.detailsBox}>
                    <pre className={classes.detailsJson}>{JSON.stringify(entry.raw, null, 2)}</pre>
                </Box>
            </Collapse>
        </>
    );
};

export const ImportGroupPolicyDialog = ({ open = false, groupId, policyCommands, onClose }: Props) => {
    const { t, i18n } = useTranslation(["modals", "common"]);
    const { classes } = useStyles();
    const { updatePolicyCommands } = usePolicyCommands();

    const [formKey, setFormKey] = React.useState(0);
    const [parseState, setParseState] = React.useState<ParseState>({ status: "idle" });

    React.useEffect(() => {
        // Remount the form on every open so a previously selected file does not leak into the next import
        if (open) {
            setParseState({ status: "idle" });
            setFormKey((key) => key + 1);
        }
    }, [open]);

    const handleFileChange = React.useCallback(
        async (file: File | undefined) => {
            if (!file) {
                setParseState({ status: "idle" });
                return;
            }

            let fileContents: string | null = null;
            try {
                fileContents = await readFileContents(file);
            } catch (error: unknown) {
                if (error instanceof Error) {
                    showErrorToast(error.message);
                } else {
                    showErrorToast(t("modals:importGroupPolicy.toast.pleaseTryAgain"));
                }
                setParseState({ status: "idle" });
                return;
            }

            if (fileContents === null) {
                showErrorToast(t("modals:importGroupPolicy.toast.pleaseTryAgain"));
                setParseState({ status: "idle" });
                return;
            }

            setParseState(parseFileContents(fileContents));
        },
        [t],
    );

    const previewEntries = React.useMemo((): PreviewEntry[] => {
        if (parseState.status !== "ready") {
            return [];
        }

        return parseState.entries.map((entry) => ({
            ...entry,
            labels: toPreviewEntryLabels(entry),
        }));
    }, [parseState, i18n.language]);

    const importableCommands =
        parseState.status === "ready" ? parseState.entries.flatMap((entry) => (entry.payload ? [entry.payload] : [])) : [];

    const hasNoImportableCommands = parseState.status === "ready" && !importableCommands.length;

    const onSubmit = async () => {
        if (!importableCommands.length) {
            return;
        }

        const combinedCommands = [...policyCommands, ...importableCommands];
        await updatePolicyCommands({ groupId, policy: JSON.stringify(combinedCommands) });
        onClose();
    };
    const onValidate = (values: FormValues) => {
        if (!values.files?.length) {
            return { files: t("modals:importGroupPolicy.input.file.validation.required") };
        }
    };

    return (
        <Form<FormValues>
            key={formKey}
            onSubmit={onSubmit}
            validate={onValidate}
            render={({ handleSubmit, form, submitting }) => (
                <form onSubmit={handleSubmit} id="editCommand">
                    <Modal
                        title={t("modals:importGroupPolicy.title")}
                        open={open}
                        onClose={onClose}
                        description={t("modals:importGroupPolicy.description")}
                        endButton={
                            <PrimaryButton
                                onClick={form.submit}
                                disabled={submitting || !importableCommands.length}
                                loading={submitting}
                            >
                                {t("common:button.import")}
                            </PrimaryButton>
                        }
                    >
                        <SelectFileField
                            name="files"
                            accept=".json,application/json"
                            showSelectedFileName
                            onFileSelected={handleFileChange}
                            label={
                                <Trans ns="modals" i18nKey="importGroupPolicy.input.file.dropzone" components={{ u: <u /> }} />
                            }
                            className={classes.fileField}
                        />

                        {parseState.status === "error" ? <ParseErrorAlert errorKey={parseState.errorKey} /> : null}

                        {parseState.status === "ready" ? (
                            <Box className={classes.previewContainer}>
                                <Typography variant="subtitle2">
                                    {t("modals:importGroupPolicy.preview.commandsFound", {
                                        count: parseState.entries.length,
                                    })}
                                </Typography>
                                {hasNoImportableCommands ? (
                                    <CustomAlert severity="warning" className={classes.noImportableAlert}>
                                        {t("modals:importGroupPolicy.preview.noImportableCommands")}
                                    </CustomAlert>
                                ) : null}
                                <List dense className={classes.previewList}>
                                    {previewEntries.map((entry, index) => (
                                        <PolicyImportPreviewItem
                                            key={`${index}-${entry.commandName ?? "invalid"}`}
                                            entry={entry}
                                            index={index}
                                            divider={index < previewEntries.length - 1}
                                        />
                                    ))}
                                </List>
                            </Box>
                        ) : null}
                    </Modal>
                </form>
            )}
        />
    );
};
